import type { MiHistorial } from '@/lib/supabase/tipos-bd'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Cargando } from '@/shared/ui/Estados/Cargando'
import { fechaHora } from '@/shared/utilidades/formato'

import { useUrlesDeEvidencia } from './useEvidencia'
import { useMiHistorial } from './useMiHistorial'
import estilos from './PaginaMiHistorial.module.css'

/** Las fotos de un trabajo, con su URL firmada (bucket privado). */
function FotosEvidencia({ rutas }: { rutas: string[] }) {
  const { urles } = useUrlesDeEvidencia(rutas)

  if (rutas.length === 0) return null

  return (
    <div className={estilos.fotos}>
      {urles.map((url, i) => (
        <a key={url} href={url} target="_blank" rel="noreferrer" className={estilos.foto}>
          <img src={url} alt={`Evidencia ${i + 1}`} loading="lazy" />
        </a>
      ))}
    </div>
  )
}

function TarjetaHistorial({ t }: { t: MiHistorial }) {
  const horas = t.horas_ejecutadas + t.horas_extra

  return (
    <article className={estilos.tarjeta}>
      <header className={estilos.encabezado}>
        <span className={estilos.numero}>{t.numero}</span>
        <span className={estilos.fecha}>{t.fecha_fin_real ? fechaHora(t.fecha_fin_real) : '—'}</span>
      </header>

      <p className={estilos.cliente}>{t.cliente ?? 'Sin cliente'}</p>
      <p className={estilos.direccion}>
        {t.direccion}
        {t.zona && <span className={estilos.zona}> · {t.zona}</span>}
      </p>

      <div className={estilos.datos}>
        {(t.servicio_nombre || t.servicio_codigo) && (
          <span className={estilos.servicio}>
            {t.servicio_codigo && <span className={estilos.codigo}>{t.servicio_codigo}</span>}
            {t.servicio_nombre}
          </span>
        )}
        <span className={estilos.horas}>{horas} h</span>
      </div>

      {t.resumen_trabajo && (
        <p className={estilos.resumen}>
          <span className={estilos.resumenRotulo}>Lo hecho</span>
          {t.resumen_trabajo}
        </p>
      )}

      <FotosEvidencia rutas={t.fotos} />
    </article>
  )
}

/**
 * El historial del operario: sus trabajos terminados, con evidencia.
 *
 * Solo para mirar. Es su registro de lo hecho —foto y comentario—, lo mismo que
 * después va en el acta. No se puede editar nada.
 */
export function PaginaMiHistorial() {
  const { historial, cargando, error } = useMiHistorial()

  if (cargando) return <Cargando mensaje="CONSULTANDO HISTORIAL TÉCNICO..." />
  if (error) return <Aviso tipo="error">{error}</Aviso>

  if (historial.length === 0) {
    return (
      <div className={estilos.vacio}>
        <p className={estilos.vacioTitulo}>Todavía no hay trabajos terminados</p>
        <p className={estilos.vacioTexto}>
          Cuando termine un trabajo —con su foto y comentario— queda aquí para siempre.
        </p>
      </div>
    )
  }

  return (
    <div className={estilos.pagina}>
      {historial.map((t) => (
        <TarjetaHistorial key={t.orden_trabajo_id} t={t} />
      ))}
    </div>
  )
}
